<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <!-- Show navigation on all pages except login -->
    <nav v-if="$route.path !== '/login'" class="bg-slate-800 text-white shadow-lg">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center gap-4 h-16">
          <div class="flex items-center min-w-0">
            <h1 class="text-lg sm:text-xl font-semibold truncate">Admin Dashboard</h1>
          </div>
          <div class="hidden md:flex items-center gap-4 lg:gap-8">
            <!-- Navigation Links -->
            <div class="flex items-center gap-2">
              <router-link
                v-for="item in navigationItems"
                :key="item.to"
                :to="item.to"
                class="px-3 py-2 rounded-md text-sm font-medium hover:bg-slate-700 transition-colors whitespace-nowrap"
                :class="{ 'bg-slate-700': $route.path === item.to }"
              >
                {{ item.label }}
              </router-link>
            </div>

            <!-- User Info and Logout -->
            <div class="flex items-center gap-4 pl-4 lg:pl-8 border-l border-slate-600">
              <span class="text-sm text-slate-300 whitespace-nowrap">{{ userName }}</span>
              <button
                @click="handleLogout"
                class="px-3 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>

          <button
            type="button"
            class="md:hidden inline-flex items-center justify-center rounded-md p-2 text-slate-200 hover:bg-slate-700 hover:text-white transition-colors"
            :aria-expanded="mobileMenuOpen ? 'true' : 'false'"
            aria-label="Toggle navigation menu"
            @click="mobileMenuOpen = !mobileMenuOpen"
          >
            <svg v-if="!mobileMenuOpen" class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </div>

        <div v-if="mobileMenuOpen" class="md:hidden border-t border-slate-700 py-4 space-y-2">
          <router-link
            v-for="item in navigationItems"
            :key="item.to"
            :to="item.to"
            class="block px-3 py-2 rounded-md text-sm font-medium hover:bg-slate-700 transition-colors"
            :class="{ 'bg-slate-700': $route.path === item.to }"
            @click="closeMobileMenu"
          >
            {{ item.label }}
          </router-link>

          <div class="pt-3 mt-3 border-t border-slate-700 space-y-3">
            <div class="px-3 text-sm text-slate-300 truncate">{{ userName }}</div>
            <div class="px-3">
              <button
                @click="handleLogout"
                class="w-full px-3 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <main class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <router-view />
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { logout } from '@/services/auth'

const router = useRouter()
const route = useRoute()
const userName = ref('User') // Default user name - EasyAuth will handle the real authentication
const mobileMenuOpen = ref(false)
const navigationItems = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/apikeys', label: 'API Keys' },
  { to: '/plans', label: 'Plans' },
  { to: '/storage', label: 'Azure Storage' },
  { to: '/test', label: 'Function Test' },
  { to: '/shop-test', label: 'Shop Test' }
]

// No authentication check - EasyAuth handles this at the API level

watch(() => route.path, () => {
  mobileMenuOpen.value = false
})

function closeMobileMenu() {
  mobileMenuOpen.value = false
}

// Handle logout
async function handleLogout() {
  try {
    mobileMenuOpen.value = false
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout failed:', error)
  }
}


</script>
